import React, { useMemo, Fragment, useState, useEffect } from "react"
import {
  Container,
  Col,
  Row,
  Button,
  Card,
  CardBody,
  Input,
  Modal,
  ModalBody,
  Form,
  Label,
} from "reactstrap"
import { useQuery, useMutation } from "react-query"
import { Link, useNavigate } from "react-router-dom"
import { useTable, useAsyncDebounce } from "react-table"
//Import Breadcrumb
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import { GENERAL } from "api/general"
import {
  ROLES,
  ROLE_LABELS,
  CREATABLE_ROLES,
  ROLES_NEEDING_STATE,
  ROLES_NEEDING_LGA,
  getUserRole,
  getAuthUser,
} from "constants/roles"
//i18n
import { withTranslation } from "react-i18next"
import Swal from "sweetalert2"
import dayjs from "dayjs"
import * as xlsx from "xlsx"
import saveAs from "file-saver"
import { unzipSync, zipSync } from "fflate"
import PaginatedDataTable from "components/PaginatedDataTable"

const UserList = props => {
  const currentUserRole = getUserRole()
  const authUser = getAuthUser() || {}
  const creatableRoles = CREATABLE_ROLES[currentUserRole] || []

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [data, setData] = useState([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [filter, setFilter] = useState("")

  const [nin, setNin] = useState("")
  const [firstname, setFirstname] = useState("")
  const [lastname, setLastname] = useState("")
  const [phone, setPhone] = useState("")
  const [email, setEmail] = useState("")
  const [states, setStates] = useState([])
  const [lgas, setLgas] = useState([])

  const [selectedState, setSelectedState] = useState("")
  const [selectedLga, setSelectedLga] = useState("")
  const [selectedRole, setSelectedRole] = useState(null)
  const [show, setShow] = useState(false)
  const [isNinVerified, setIsNinVerified] = useState(false)
  const [showBulkModal, setShowBulkModal] = useState(false)
  const [bulkRows, setBulkRows] = useState([])
  const [bulkErrors, setBulkErrors] = useState([])
  const [isEmailErr, setIsEmailErr] = useState(true)
  const [isStateErr, setIsStateErr] = useState(true)
  const [isLgaErr, setIsLgaErr] = useState(true)
  const [isRoleErr, setIsRoleErr] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    getStates
  }, [])

  const handleNINChange = evt => {
    const value = evt.target.value
    // Ensure the value only contains numbers and has a maximum length of 11
    if (/^\d{0,11}$/.test(value)) {
      setNin(value)
    }
  }

  const [value, setValue] = React.useState("")
  const onChange = useAsyncDebounce(value => {
    setFiilter(value || undefined)
  }, 200)

  const handleRoleChange = evt => {
    setSelectedRole(evt.target.value)
  }

  const fetchRecords = async (page, pageSize, filter) => {
    Swal.fire({
      title: "Fetching records...",
      showConfirmButton: false,
      allowEscapeKey: false,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading(Swal.getDenyButton())
      },
    })

    console.log("fetching records with params ::", { page, pageSize, filter })
    try {
      const response = await getAdminList.mutateAsync({
        page,
        pageSize,
        searchKey: filter ?? "",
        stateId: getAuthUser()?.state ?? "",
      })
      Swal.close()
      setData(response.palliative)
      setTotalRecords(response.totalRecords)
    } catch (error) {
      Swal.fire({
        title: "Error fetching records",
        text: error.message,
        icon: "error",
      })
    }
  }

  useEffect(() => {
    fetchRecords(page, pageSize, filter)
  }, [page, pageSize])

  const getAdminList = useMutation({
    mutationFn: async ({ page, pageSize, searchKey, stateId }) => {
      return GENERAL.getAdminListFn({ page, pageSize, searchKey, stateId })
    },
    onError: error => {
      Swal.fire("Error fetching records", error.message, "error")
    },
  })

  const handlePageChange = page => {
    setPage(page)
  }

  const handlePerRowsChange = (newPageSize, page) => {
    setPageSize(newPageSize)
    setPage(1) // Reset to first page whenever page size changes
  }

  const handleSearchFn = async () => {
    Swal.fire({
      title: "Searching records...",
      showConfirmButton: false,
      allowEscapeKey: false,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading(Swal.getDenyButton())
      },
    })
    setPage(1) // Reset to first page when searching
    await fetchRecords(1, pageSize, filter.trim())
  }
  const startIndex = (page - 1) * pageSize

  const getStates = useQuery({
    queryKey: ["getStates"],
    queryFn: () => {
      return GENERAL.getStates()
    },
    onSuccess: async res => {
      if (res?.palliative) {
        setStates(res.palliative)
      }
    },
  })

  const getLgas = useMutation({
    mutationFn: payload => {
      return GENERAL.getLgas(payload.state_id || authUser?.state)
    },
    onSuccess: async res => {
      Swal.close()
      if (res?.palliative) {
        setLgas(res.palliative)
      }
    },
  })

  const handlePhoneChange = e => {
    const input = e.target.value
    // Remove any non-digit characters
    const digitsOnly = input.replace(/\D/g, "")
    // Update the state with the cleaned value
    setPhone(digitsOnly)
  }

  const createUser = useMutation({
    mutationFn: payload => {
      return GENERAL.createUser(payload)
    },
    onSuccess: async res => {
      Swal.close()
      if (res.status == 422) {
        Swal.fire("Process Failed!", res.data.message.join(", "), "error").then(
          res => {
            if (res.isConfirmed) {
              showModal()
            }
          }
        )
      }
      if (res.status == 403) {
        Swal.fire("Process Failed!", res.data.message, "error").then(res => {
          if (res.isConfirmed) {
            showModal()
          }
        })
      }
      if (res.status == 201) {
        Swal.fire("Process Successful!", res.data.message, "success").then(
          () => {
            fetchRecords(page, pageSize, filter)
          }
        )
      }
    },
  })

  const handleSelectState = evt => {
    setSelectedState(evt.target.value)
    if (ROLES_NEEDING_LGA.includes(selectedRole)) {
      Swal.fire({
        title: "Fetching LGAs...",
        showConfirmButton: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading(Swal.getDenyButton())
        },
      })
      const getLgaDto = {
        state_id: evt.target.value,
      }
      getLgas.mutate(getLgaDto)
    }
  }

  const handleFetchLgasForState = role => {
    if (ROLES_NEEDING_LGA.includes(role)) {
      Swal.fire({
        title: "Fetching LGAs...",
        showConfirmButton: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading(Swal.getDenyButton())
        },
      })
      const getLgaDto = {
        state_id: authUser?.state ?? "",
      }
      getLgas.mutate(getLgaDto)
    }
  }

  // Pre-fill state for STATE_ADMIN from their own authUser profile
  useEffect(() => {
    if (currentUserRole === ROLES.STATE_ADMIN && authUser?.state) {
      setSelectedState(authUser.state)
    }
  }, [])

  const handleSelectLga = evt => {
    setSelectedLga(evt.target.value)
  }

  const handleSubmit = () => {
    if (!email) {
      return Swal.fire("Process Failed!", "Email is required.", "error")
    }
    setIsEmailErr(false)

    if (selectedRole == null) {
      return Swal.fire("Process Failed!", "Role is required.", "error")
    }
    setIsRoleErr(false)

    // State check
    if (ROLES_NEEDING_STATE.includes(selectedRole) && !selectedState) {
      return Swal.fire("Process Failed!", "State is required.", "error")
    }
    setIsStateErr(false)

    // LGA check
    if (ROLES_NEEDING_LGA.includes(selectedRole) && !selectedLga) {
      return Swal.fire("Process Failed!", "LGA is required.", "error")
    }
    setIsLgaErr(false)

    // Email domain check
    if (
      !email.toLocaleLowerCase().includes("@") ||
      email.toLocaleLowerCase().includes("@nimc.gov.ng")
    ) {
      return Swal.fire("Process Failed!", "Email is not valid", "error")
    }

    closeModal()
    const createUserDto = {
      firstname,
      lastname,
      email,
      phone,
      role: selectedRole,
      state: selectedState,
      nin,
      lga: selectedLga,
      zone: "0",
    }

    Swal.fire({
      title: "Creating user...",
      showConfirmButton: false,
      allowEscapeKey: false,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading(Swal.getDenyButton())
      },
    })
    createUser.mutate(createUserDto)
  }

  document.title = "ManageUsers - Ward Registrars Management System"

  const columns = [
    {
      name: "#",
      sortable: true,
      selector: (row, index) => startIndex + index + 1,
      width: "4rem",
    },
    {
      name: "Role",
      sortable: true,
      selector: row => ROLE_LABELS[row.role] || row.role,
    },
    {
      name: "NIN",
      sortable: true,
      selector: row => row.nin,
    },
    {
      name: "First Name",
      sortable: true,
      selector: row => row.first_name,
    },
    {
      name: "Last Name",
      sortable: true,
      selector: row => row.last_name,
    },
    {
      name: "Email",
      sortable: true,
      selector: row => row.email,
      wrap: true,
      width: "14rem",
    },
    {
      name: "State of Residence",
      sortable: true,
      selector: row => row.state,
    },
    {
      name: "LGA of Residence",
      sortable: true,
      selector: row => row.lga,
    },
    {
      name: "Date Created",
      sortable: true,
      selector: row => new Date(row.created_at).toLocaleDateString(),
    },
    {
      name: "Status",
      sortable: true,
      selector: row => (
        <span style={{ color: row.status === "Active" ? "green" : "red" }}>
          {row.status}
        </span>
      ),
    },
    {
      name: "Action",
      sortable: false,
      selector: row => (
        <button
          onClick={() => handleStatusChange(row.id, row.status)}
          style={{
            backgroundColor: row.status === "Active" ? "#ef233c" : "green",
            color: "white",
            border: "none",
          }}
        >
          {row.status === "Active" ? "Deactivate" : "Activate"}
        </button>
      ),
    },
  ]

  const handleStatusChange = async (userId, currentStatus) => {
    const payload = { userId: userId }

    // Call the appropriate function based on the current status
    if (currentStatus === "Inactive") {
      await activateUser.mutate(payload)
    } else if (currentStatus === "Active") {
      await deactivateUser.mutate(payload)
    }
  }

  const activateUser = useMutation({
    mutationFn: async payload => {
      Swal.fire({
        title: "Activating user...",
        showConfirmButton: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading(Swal.getDenyButton())
        },
      })
      return GENERAL.activateUser(payload)
    },
    onError: error => {
      Swal.close()
      Swal.fire("Error activating user", error.message, "error")
    },
    onSuccess: res => {
      const message = res.data.palliative.msg
      Swal.close()
      Swal.fire("Success", message, "success").then(() => {
        fetchRecords(page, pageSize, filter)
      })
    },
  })

  const deactivateUser = useMutation({
    mutationFn: async payload => {
      Swal.fire({
        title: "Deactivating user...",
        showConfirmButton: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading(Swal.getDenyButton())
        },
      })
      return GENERAL.deactivateUser(payload)
    },
    onError: error => {
      Swal.close()
      Swal.fire("Error deactivating user", error.message, "error")
    },
    onSuccess: res => {
      const message = res.data.palliative.msg
      Swal.close()
      Swal.fire("Success", message, "success").then(() => {
        fetchRecords(page, pageSize, filter)
      })
    },
  })

  const showModal = () => {
    setShow(true)
  }

  const closeModal = () => {
    setIsNinVerified(false)
    setEmail("")
    setShow(false)
  }

  const handleVerifyNinFn = () => {
    Swal.fire({
      title: "Verifying NIN...",
      showConfirmButton: false,
      allowEscapeKey: false,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading(Swal.getDenyButton())
      },
    })
    const verifyNinDto = {
      nin: nin,
    }
    verifyNinFn.mutate(verifyNinDto)
  }

  const verifyNinFn = useMutation({
    mutationFn: payload => {
      return GENERAL.ninVerifyFn(payload)
    },
    onSuccess: async res => {
      Swal.close()
      if (res?.data?.palliative?.statusCode == 200) {
        setFirstname(res?.data?.palliative?.firstname)
        setLastname(res?.data?.palliative?.lastname)
        setPhone(res?.data?.palliative?.phone)
        setIsNinVerified(true)
      }
      if (
        res?.data?.palliative?.statusCode == 400 &&
        res?.data?.palliative?.msg == "NIN verification failed"
      ) {
        Swal.fire(res?.data?.palliative?.msg, "", "error")
        setIsNinVerified(false)
      }
      if (res?.data?.palliative?.statusCode == 500) {
        Swal.fire("Process Failed!", res?.data?.palliative?.msg, "error")
      }
    },
  })

  const bulkCreateFn = useMutation({
    mutationFn: payload => GENERAL.bulkCreateUsers(payload),
    onSuccess: res => {
      Swal.close()
      const data = res.data
      if (res.status === 201 || res.status === 200) {
        const results = data?.results || []
        const hasFailures = results.some(r => r.status === "failed")
        const html = results.length
          ? `<div style="max-height:300px;overflow-y:auto;text-align:left;font-size:13px">` +
            results.map(r =>
              `<div style="padding:6px 0;border-bottom:1px solid #eee">
                <strong>${r.email || r.nin || ""}</strong><br/>
                <span style="color:${r.status === "failed" ? "#f46a6a" : "#34c38f"}">${r.reason || r.status}</span>
              </div>`
            ).join("") +
            `</div>`
          : ""
        Swal.fire({
          title: data?.message || "Done!",
          html: html || undefined,
          icon: hasFailures ? "warning" : "success",
        }).then(() => {
          setShowBulkModal(false)
          setBulkRows([])
          setBulkErrors([])
          fetchRecords(page, pageSize, filter)
        })
      } else {
        Swal.fire("Error", data?.message || "Something went wrong.", "error")
      }
    },
  })

  const downloadTemplate = () => {
    const wb = xlsx.utils.book_new()
    const ws1 = xlsx.utils.aoa_to_sheet([
      ["nin", "firstname", "lastname", "email", "role", "state", "lga", "zone"],
      ["", "", "", "", "", "", "", ""],
    ])
    xlsx.utils.book_append_sheet(wb, ws1, "Users")
    xlsx.utils.book_append_sheet(
      wb,
      xlsx.utils.aoa_to_sheet([["Valid Roles"], ...creatableRoles.map(r => [r])]),
      "Role Options"
    )
    // SheetJS CE doesn't write data validations, so inject the dropdown XML directly
    const buf = xlsx.write(wb, { bookType: "xlsx", type: "array" })
    const unzipped = unzipSync(new Uint8Array(buf))
    const dec = new TextDecoder()
    const enc = new TextEncoder()
    let sheetXml = dec.decode(unzipped["xl/worksheets/sheet1.xml"])
    const dv =
      `<dataValidations count="1">` +
      `<dataValidation type="list" allowBlank="1" showInputMessage="1" showErrorMessage="1" sqref="E2:E1048576">` +
      `<formula1>"${creatableRoles.join(",")}"</formula1>` +
      `</dataValidation></dataValidations>`
    sheetXml = sheetXml.replace("</worksheet>", `${dv}</worksheet>`)
    unzipped["xl/worksheets/sheet1.xml"] = enc.encode(sheetXml)
    const zipped = zipSync(unzipped, { level: 6 })
    saveAs(
      new Blob([zipped], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      "bulk-users-template.xlsx"
    )
  }

  const handleBulkFile = e => {
    const file = e.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = ev => {
      const wb = xlsx.read(new Uint8Array(ev.target.result), { type: "array" })
      const rows = xlsx.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]).map(row => ({
        ...row,
        nin: String(row.nin ?? ""),
        state: String(row.state ?? ""),
        lga: String(row.lga ?? ""),
        zone: String(row.zone ?? ""),
      }))
      const errs = rows
        .map((row, i) => {
          if (!row.nin || row.nin.length !== 11) return `Row ${i + 2}: nin must be exactly 11 digits`
          if (!row.role || !creatableRoles.includes(row.role))
            return `Row ${i + 2}: invalid role "${row.role}"`
          return null
        })
        .filter(Boolean)
      setBulkRows(rows)
      setBulkErrors(errs)
    }
    reader.readAsArrayBuffer(file)
  }

  const handleBulkSubmit = () => {
    if (bulkErrors.length) return
    bulkCreateFn.mutate({ users: bulkRows })
  }

  return (
    <React.Fragment>
      <Modal size="lg" isOpen={showBulkModal} centered={true}>
        <ModalBody className="py-3 px-5">
          <Row style={{ paddingTop: "25px", paddingBottom: "25px" }}>
            <Col lg={12} style={{ marginTop: "10px", marginBottom: "10px" }}>
              <div className="d-flex justify-content-between">
                <h5>Bulk Upload Users</h5>
                <div onClick={() => { setShowBulkModal(false); setBulkRows([]); setBulkErrors([]) }}>
                  <i className="bx bx-x" style={{ fontSize: 24, cursor: "pointer" }}></i>
                </div>
              </div>
            </Col>
            <Col lg={12}>
              <p className="text-muted">
                Download the template, fill it in offline, then re-upload. The <strong>Role</strong> column has a dropdown with valid options.
              </p>
              <Button color="secondary" outline size="sm" className="mb-3" onClick={downloadTemplate}>
                Download Template
              </Button>
              <div className="mb-3">
                <Label>Upload filled template (.xlsx)</Label>
                <Input type="file" accept=".xlsx" onChange={handleBulkFile} />
              </div>
              {bulkErrors.length > 0 && (
                <div className="mb-3">
                  {bulkErrors.map((err, i) => (
                    <p key={i} className="text-danger mb-1" style={{ fontSize: 13 }}>{err}</p>
                  ))}
                </div>
              )}
              {bulkRows.length > 0 && (
                <div className="table-responsive mb-3">
                  <p className="mb-1"><strong>{bulkRows.length}</strong> row(s) parsed</p>
                  <table className="table table-sm table-bordered">
                    <thead>
                      <tr>
                        <th>NIN</th>
                        <th>First Name</th>
                        <th>Last Name</th>
                        <th>Email</th>
                        <th>Role</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkRows.map((row, i) => (
                        <tr key={i}>
                          <td>{row.nin}</td>
                          <td>{row.firstname}</td>
                          <td>{row.lastname}</td>
                          <td>{row.email}</td>
                          <td>{row.role}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="text-center">
                <Button
                  color="danger"
                  outline
                  className="me-2"
                  onClick={() => { setShowBulkModal(false); setBulkRows([]); setBulkErrors([]) }}
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  disabled={bulkRows.length === 0 || bulkErrors.length > 0 || bulkCreateFn.isLoading}
                  onClick={handleBulkSubmit}
                >
                  {bulkCreateFn.isLoading ? "Uploading..." : "Upload Users"}
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>

      <Modal size="lg" isOpen={show} centered={true}>
        <ModalBody className="py-3 px-5">
          <Row style={{ paddingTop: "25px", paddingBottom: "25px" }}>
            <Col lg={12} style={{ marginTop: "10px", marginBottom: "10px" }}>
              <div
                className=""
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                <h5>Create User</h5>

                <div onClick={closeModal}>
                  <i
                    className="bx bx-x"
                    style={{ fontSize: 24, cursor: "pointer" }}
                  ></i>
                </div>
              </div>
            </Col>
            <Form>
              {!isNinVerified && (
                <Row>
                  <Col md={9}>
                    <div className="mb-3">
                      <Label htmlFor="formrow-email-Input">
                        NIN <span className="text-danger">*</span>
                      </Label>
                      <div className="col-md-12">
                        <Input
                          type="text"
                          placeholder="Enter NIN"
                          maxLength={11}
                          value={nin}
                          name="nin"
                          onChange={handleNINChange}
                        />
                      </div>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center mt-3">
                      {!verifyNinFn.isLoading && (
                        <button
                          type="submit"
                          className="btn btn-primary w-80 ml-5"
                          onClick={handleVerifyNinFn}
                        >
                          VERIFY NIN
                        </button>
                      )}
                      {verifyNinFn.isLoading && (
                        <button
                          type="submit"
                          className="btn btn-primary w-80 ml-5"
                        >
                          VERIFYING NIN
                        </button>
                      )}
                    </div>
                  </Col>
                </Row>
              )}
              {isNinVerified && (
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label htmlFor="formrow-email-Input">
                        First Name <span className="text-danger">*</span>
                      </Label>
                      <div className="col-md-12">
                        <Input
                          type="text"
                          placeholder="Enter First Name"
                          name="firstname"
                          value={firstname}
                          disabled
                          onChange={evt => setFirstname(evt.target.value)}
                        />
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label htmlFor="formrow-email-Input">
                        Last Name <span className="text-danger">*</span>
                      </Label>
                      <div className="col-md-12">
                        <Input
                          type="text"
                          name="lastname"
                          value={lastname}
                          disabled
                          placeholder="Enter Last Name"
                          onChange={evt => setLastname(evt.target.value)}
                        />
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label htmlFor="formrow-email-Input">
                        Phone Number <span className="text-danger">*</span>
                      </Label>
                      <div className="col-md-12">
                        <Input
                          type="text"
                          name="phone"
                          value={phone}
                          placeholder="Enter Phone Number"
                          maxLength={11}
                          onChange={handlePhoneChange}
                        />
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label htmlFor="formrow-email-Input">
                        Email <span className="text-danger">*</span>
                      </Label>
                      <div className="col-md-12">
                        <Input
                          type="text"
                          name="email"
                          value={email}
                          required={true}
                          placeholder="Enter Email"
                          onChange={evt => setEmail(evt.target.value)}
                        />
                      </div>
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="mb-3">
                      <Label htmlFor="formrow-password-Input">
                        Role <span className="text-danger">*</span>
                      </Label>
                      <div className="col-md-12">
                        <select
                          onChange={evt => {
                            handleRoleChange(evt)
                            handleFetchLgasForState(evt.target.value)
                          }}
                          className="form-control"
                          value={selectedRole || ""}
                        >
                          <option value="">Select</option>
                          {creatableRoles.map(role => (
                            <option key={role} value={role}>
                              {ROLE_LABELS[role]}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </Col>

                  {selectedRole &&
                    ROLES_NEEDING_STATE.includes(selectedRole) && (
                      <Col md={6}>
                        <div className="mb-3">
                          <Label htmlFor="formrow-password-Input">
                            State <span className="text-danger">*</span>
                          </Label>
                          <div className="col-md-12">
                            {currentUserRole === ROLES.STATE_ADMIN ? (
                              <Input
                                type="text"
                                className="form-control"
                                value={authUser?.stateName || ""}
                                disabled
                              />
                            ) : (
                              <select
                                onChange={handleSelectState}
                                className="form-control"
                              >
                                <option>Select</option>
                                {states?.map(state => (
                                  <option key={state.id} value={state.id}>
                                    {state.name}
                                  </option>
                                ))}
                              </select>
                            )}
                          </div>
                        </div>
                      </Col>
                    )}
                  {selectedRole && ROLES_NEEDING_LGA.includes(selectedRole) && (
                    <Col md={6}>
                      <div className="mb-3">
                        <Label htmlFor="formrow-password-Input">
                          LGA <span className="text-danger">*</span>
                        </Label>
                        <div className="col-md-12">
                          <select
                            onChange={handleSelectLga}
                            className="form-control"
                          >
                            <option>Select</option>
                            {lgas?.map(lga => (
                              <option key={lga.id} value={lga.id}>
                                {lga.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </Col>
                  )}
                </Row>
              )}
            </Form>
          </Row>
          {isNinVerified && (
            <Row>
              <div className="text-center">
                <button
                  style={{ marginRight: 10 }}
                  className="btn btn-danger w-80 mr-2"
                  onClick={() => closeModal()}
                >
                  CANCEL
                </button>
                <button
                  type="submit"
                  className="btn btn-primary w-80 ml-5"
                  onClick={handleSubmit}
                >
                  SUBMIT
                </button>
              </div>
            </Row>
          )}
        </ModalBody>
      </Modal>

      <div className="page-content">
        <Container fluid className="container-xxl" style={{ minWidth: "97%" }}>
          {/* Render Breadcrumb */}
          <Row>
            <Breadcrumbs
              title={props.t("Account Management")}
              breadcrumbItem={props.t("Manage Users")}
              hasBackBtn={true}
            />
          </Row>

          <Card>
            <CardBody>
              <div className="mb-4 h4 card-title">Account Management</div>
              <Fragment>
                <Row className="mb-2">
                  <Col md={10}>
                    <React.Fragment>
                      <div className="search-box me-xxl-2 my-3 my-xxl-0 d-inline-block">
                        <div
                          className="position-relative"
                          style={{ display: "flex", alignItems: "center" }}
                        >
                          <label
                            htmlFor="search-bar-0"
                            className="search-label"
                          >
                            <span id="search-bar-0-label" className="sr-only">
                              Search this table
                            </span>
                            <input
                              onChange={e => setFilter(e.target.value)}
                              id="search-bar-0"
                              type="text"
                              className="form-control"
                              placeholder={`Search table`}
                              value={filter || ""}
                            />
                          </label>
                          <i
                            className="bx bx-search-alt search-icon"
                            style={{ cursor: "pointer" }}
                            onClick={handleSearchFn}
                          ></i>
                          <div
                            className="d-inline-block"
                            style={{ marginLeft: 5, marginBottom: 8 }}
                          >
                            <Button
                              type="button"
                              color="primary"
                              className="btn-md btn-rounded"
                              onClick={handleSearchFn}
                            >
                              Search
                            </Button>
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  </Col>

                  <Col>
                    <div className="d-flex justify-content-end align-items-center gap-2" style={{ whiteSpace: "nowrap" }}>
                      {creatableRoles.length > 0 && (
                        <>
                          <Button
                            type="button"
                            color="success"
                            onClick={() => setShowBulkModal(true)}
                          >
                            Bulk Upload
                          </Button>
                          <Button
                            type="button"
                            color="primary"
                            onClick={() => showModal()}
                          >
                            Create User
                          </Button>
                        </>
                      )}
                    </div>
                  </Col>
                </Row>

                <PaginatedDataTable
                  columns={columns}
                  data={data}
                  totalRecords={totalRecords}
                  handlePageChange={handlePageChange}
                  handlePerRowsChange={handlePerRowsChange}
                />
              </Fragment>
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default withTranslation()(UserList)
